import { atom, useSetAtom } from "jotai";
import type { ReactNode } from "react";
import { useEffect } from "react";

import type { ActionGroup } from "@/actions/action-types";

/**
 * Global atom holding all dynamically registered command palette action groups.
 * Each registration is keyed by a unique source ID to allow clean add/remove.
 */
type Registration = {
    sourceId: string;
    groups: ActionGroup[];
    /** Optional context badge rendered above the groups (e.g. environment name). */
    contextBadge?: ReactNode;
};

const registrationsAtom = atom<Registration[]>([]);

/**
 * Derived atom that flattens all registrations into a sorted list of groups.
 */
export const commandPaletteGroupsAtom = atom<ActionGroup[]>((get) => {
    const registrations = get(registrationsAtom);
    const allGroups = registrations.flatMap((r) => r.groups);
    return allGroups.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
});

/**
 * Derived atom that collects all context badges from registrations.
 */
export const commandPaletteBadgesAtom = atom<ReactNode[]>((get) => {
    const registrations = get(registrationsAtom);
    return registrations.filter((r) => r.contextBadge).map((r) => r.contextBadge);
});

type RegisterOptions = {
    /** Optional context badge rendered above the groups. */
    contextBadge?: ReactNode;
};

/**
 * Register action groups in the command palette. Groups are added on mount
 * and removed on unmount. The `sourceId` must be stable across renders.
 *
 * Usage:
 * ```ts
 * const actions = useEnvironmentActions(envId);
 * useRegisterCommandPaletteActions("env-details", [
 *     { id: "env-actions", heading: "Environment", actions },
 * ], { contextBadge: <EnvironmentContextBadge environmentId={envId} /> });
 * ```
 */
export function useRegisterCommandPaletteActions(
    sourceId: string,
    groups: ActionGroup[],
    options?: RegisterOptions,
): void {
    const setRegistrations = useSetAtom(registrationsAtom);

    useEffect(() => {
        const contextBadge = options?.contextBadge;

        // Callers are expected to memoize `groups` and `contextBadge`; otherwise
        // this effect will churn registrations on every render.
        setRegistrations((prev) => {
            const existing = prev.find((r) => r.sourceId === sourceId);
            if (existing?.groups === groups && existing.contextBadge === contextBadge) {
                return prev;
            }

            const filtered = prev.filter((r) => r.sourceId !== sourceId);
            return [...filtered, { sourceId, groups, contextBadge }];
        });

        return () => {
            setRegistrations((prev) => {
                const filtered = prev.filter((r) => r.sourceId !== sourceId);
                return filtered.length === prev.length ? prev : filtered;
            });
        };
    }, [sourceId, groups, options?.contextBadge, setRegistrations]);
}
